import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import { isPaymentBankBlocked } from '@/utils/paymentBankAvailability'

const DYPAY_FUNCTIONS_BASE = 'https://asia-east2-connextcc.cloudfunctions.net'
const TERMINAL_STATUSES = ['SUCCESS', 'REJECTED', 'EXPIRED', 'CREATE_FAILED', 'MANUAL_REVIEW']
const TRUE_WALLET_BANK_KEYS = ['truewallet', 'truemoney', 'truemoneywallet', 'ทรูวอลเลท', 'ทรูมันนี่']

function normalizeBank (value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
}

function requestId () {
  return `dypay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

export default {
  data () {
    return {
      dypayConfig: {
        enabled: false,
        depositEnabled: false,
        availableNow: false,
        depositMin: 300,
        depositMax: 100000,
        visibleToCodes: [],
        blockedBanks: []
      },
      dypayConfigLoaded: false,
      dypayOrderId: '',
      dypayTransaction: null,
      dypayStatusTimer: null,
      dypayStatusChecking: false,
      dypayCompletionHandled: false
    }
  },
  computed: {
    hasActiveDyPayDeposit () {
      return Boolean(this.dypayOrderId && this.dypayTransaction && this.dypayTransaction.payment_url)
    },
    dypayVisibleToUser () {
      const codes = Array.isArray(this.dypayConfig.visibleToCodes)
        ? this.dypayConfig.visibleToCodes.map(code => String(code || '').trim().toUpperCase()).filter(Boolean)
        : []
      const userCode = String((this.user && this.user.code) || '').trim().toUpperCase()
      return codes.includes('*') || Boolean(userCode && codes.includes(userCode))
    },
    dypayDepositRangeText () {
      return `${Number(this.dypayConfig.depositMin || 300).toLocaleString()}-${Number(this.dypayConfig.depositMax || 100000).toLocaleString()}`
    }
  },
  watch: {
    'user.key': {
      immediate: true,
      handler (userKey) {
        if (!userKey) return
        this.loadDyPayConfig()
        this.restoreDyPayDeposit()
      }
    }
  },
  beforeDestroy () {
    this.stopDyPayStatusPolling()
  },
  methods: {
    async dypayHeaders () {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบใหม่')
      return { Authorization: `Bearer ${await currentUser.getIdToken()}` }
    },
    async loadDyPayConfig () {
      try {
        const response = await axios.get(`${DYPAY_FUNCTIONS_BASE}/dypayPublicConfig`, { timeout: 10000 })
        if (response.data && response.data.success && response.data.data) this.dypayConfig = { ...this.dypayConfig, ...response.data.data }
      } catch (error) {
        console.warn('DyPay public config unavailable:', error && error.message ? error.message : error)
      } finally {
        this.dypayConfigLoaded = true
      }
    },
    isDyPayAmountAllowed (amount) {
      const value = Number(amount || 0)
      const min = Number(this.dypayConfig.depositMin || 300)
      const max = Number(this.dypayConfig.depositMax || 100000)
      return Number.isInteger(value) && value >= min && (max <= 0 || value <= max)
    },
    isDyPayBlockedDepositAccount (account) {
      return TRUE_WALLET_BANK_KEYS.includes(normalizeBank(account && (account.bank || account.bank_name))) ||
        isPaymentBankBlocked(this.dypayConfig.blockedBanks, account)
    },
    async tryDyPayDeposit (selectedAccount) {
      if (!this.dypayConfigLoaded) await this.loadDyPayConfig()
      if (this.isDyPayBlockedDepositAccount(selectedAccount)) return false
      if (!this.dypayConfig.enabled || !this.dypayConfig.depositEnabled || this.dypayConfig.availableNow === false || !this.dypayVisibleToUser) return false
      const amount = Number.parseInt(this.inputDepositAmount, 10) || 0
      if (!this.isDyPayAmountAllowed(amount)) throw new Error(`DyPay รองรับยอดฝาก ${this.dypayDepositRangeText} บาทต่อรายการ`)
      try {
        const response = await axios.post(`${DYPAY_FUNCTIONS_BASE}/dypayCreateDeposit`, {
          userKey: this.user.key,
          amount,
          account: selectedAccount,
          bonus: this.getDepositBonusRequested(),
          clientRequestId: requestId()
        }, { headers: await this.dypayHeaders(), timeout: 45000 })
        const data = response.data && response.data.data
        if (!response.data || !response.data.success || !data || !data.order_id || (!data.payment_value && !data.payment_url)) throw new Error((response.data && response.data.error) || 'DyPay response ไม่ครบถ้วน')
        this.dypayOrderId = data.order_id
        this.dypayTransaction = data
        this.dypayCompletionHandled = false
        this.persistDyPayDeposit(data)
        this.startDyPayStatusPolling(data.order_id)
        this.depositAmount = amount
        this.depositAmountWithDecimal = amount.toLocaleString('en-US')
        this.depositDecimal = '00'
        this.assignedBank = null
        this.paybridgeMatch = null
        return true
      } catch (error) {
        const payload = error && error.response && error.response.data
        const provider = payload && payload.provider
        const message = (provider && (provider.message || provider.detail)) || (payload && payload.error) || (error && error.message)
        throw new Error(message || 'DyPay ไม่สามารถสร้างรายการฝากได้ กรุณาลองใหม่')
      }
    },
    dypayStorageKey () {
      return `connextcc_dypay_deposit_${(this.user && this.user.key) || 'guest'}`
    },
    persistDyPayDeposit (transaction) {
      if (typeof window === 'undefined' || !window.localStorage) return
      localStorage.setItem(this.dypayStorageKey(), JSON.stringify({
        order_id: transaction.order_id,
        payment_url: transaction.payment_url || '',
        request_amount: Number(transaction.request_amount || this.depositAmount || 0),
        recommended_amount: Number(transaction.recommended_amount || 0),
        stored_at: Date.now()
      }))
    },
    clearDyPayDeposit () {
      if (typeof window !== 'undefined' && window.localStorage) localStorage.removeItem(this.dypayStorageKey())
    },
    startDyPayStatusPolling (orderId) {
      this.stopDyPayStatusPolling()
      const poll = () => this.fetchDyPayDepositStatus(orderId)
      poll()
      this.dypayStatusTimer = setInterval(poll, 5000)
    },
    stopDyPayStatusPolling () {
      if (this.dypayStatusTimer) clearInterval(this.dypayStatusTimer)
      this.dypayStatusTimer = null
    },
    async fetchDyPayDepositStatus (orderId) {
      if (!orderId || !(this.user && this.user.key) || this.dypayCompletionHandled || this.dypayStatusChecking) return null
      this.dypayStatusChecking = true
      try {
        const response = await axios.post(`${DYPAY_FUNCTIONS_BASE}/dypayGetDepositStatus`, { userKey: this.user.key, orderId }, { headers: await this.dypayHeaders(), timeout: 15000 })
        const transaction = response.data && response.data.data
        if (!response.data || !response.data.success || !transaction) return null
        this.dypayTransaction = { ...(this.dypayTransaction || {}), ...transaction }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') this.handleDyPayDepositSuccess(transaction)
        else if (TERMINAL_STATUSES.includes(status)) this.handleDyPayDepositFailed(transaction, status)
        return transaction
      } catch (error) {
        return null
      } finally {
        this.dypayStatusChecking = false
      }
    },
    async restoreDyPayDeposit () {
      if (typeof window === 'undefined' || !window.localStorage || !(this.user && this.user.key)) return false
      try {
        const cached = JSON.parse(localStorage.getItem(this.dypayStorageKey()) || 'null')
        if (!cached || !cached.order_id) return false
        this.dypayOrderId = cached.order_id
        this.dypayTransaction = cached
        const status = await this.fetchDyPayDepositStatus(cached.order_id)
        if (status && !TERMINAL_STATUSES.includes(String(status.status || '').toUpperCase())) this.startDyPayStatusPolling(cached.order_id)
        return true
      } catch (error) {
        this.clearDyPayDeposit()
        return false
      }
    },
    handleDyPayDepositSuccess (transaction) {
      if (this.dypayCompletionHandled) return
      this.dypayCompletionHandled = true
      this.stopDyPayStatusPolling()
      this.clearDyPayDeposit()
      const amount = Number(transaction.actual_amount || transaction.recommended_amount || transaction.request_amount || 0)
      this.dypayOrderId = ''
      this.dypayTransaction = null
      if (this.$swal) this.$swal({ icon: 'success', title: 'ฝากเงินสำเร็จ', text: `ระบบเติมเครดิต ${amount.toLocaleString()} บาทเรียบร้อยแล้ว` })
    },
    handleDyPayDepositFailed (transaction, status) {
      if (this.dypayCompletionHandled) return
      this.dypayCompletionHandled = true
      this.stopDyPayStatusPolling()
      this.clearDyPayDeposit()
      this.dypayOrderId = ''
      this.dypayTransaction = null
      if (this.$swal) this.$swal({
        icon: status === 'MANUAL_REVIEW' ? 'warning' : 'error',
        title: status === 'MANUAL_REVIEW' ? 'กำลังตรวจสอบรายการฝาก' : 'รายการฝากไม่สำเร็จ',
        text: transaction.failed_reason || 'กรุณาติดต่อแอดมินหากได้ชำระเงินแล้ว'
      })
    },
    async cancelDyPayDeposit (options = {}) {
      const orderId = this.dypayOrderId
      if (!orderId) return true
      await this.fetchDyPayDepositStatus(orderId)
      if (!this.dypayOrderId) return false
      if (this.$swal && !options.skipConfirmation) {
        const confirmation = await this.$swal({
          icon: 'warning',
          title: 'ยกเลิกรายการและเปลี่ยนยอดฝาก?',
          text: 'หากโอนไปแล้วอย่ายกเลิก กรุณารอระบบยืนยันรายการสำเร็จ',
          showCancelButton: true,
          confirmButtonText: 'ยืนยันยกเลิก',
          cancelButtonText: 'รอทำรายการต่อ'
        })
        if (!confirmation.isConfirmed) return false
      }
      this.stopDyPayStatusPolling()
      this.clearDyPayDeposit()
      this.dypayOrderId = ''
      this.dypayTransaction = null
      this.dypayStatusChecking = false
      this.dypayCompletionHandled = false
      this.depositAmount = null
      this.depositAmountWithDecimal = null
      this.inputDepositAmount = null
      return true
    }
  }
}
